import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import {
    Contact, Phone, Mail, UserPlus, Trash2, Loader2, Building2,
    MailOpen, MailCheck, AlertCircle, Share2, RefreshCw,
} from 'lucide-react';
import { apiMethods } from '@/services/api';
import { CONNECTIONS_KEY, LIST_KEY, UNREAD_KEY } from '@/hooks/useConnections';
import { isIOS, isAndroid } from '@/features/card/lib/device';

const PAGE_SIZE = 20;

const telHref = (n) => `tel:${String(n).replace(/[^\d+]/g, '')}`;
const initialsOf = (name = '') =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('') || '?';
const safeFileName = (name) => (name || 'contact').replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-') || 'contact';

async function saveVCard(connection) {
    const res = await apiMethods.users.getConnectionVCard(connection.id);
    const blob = new Blob([res.data], { type: 'text/vcard' });
    const fileName = `${safeFileName(connection.name)}.vcf`;

    // Phones: the share sheet offers "Contacts" directly, which beats a file download.
    if ((isIOS || isAndroid) && typeof navigator.canShare === 'function') {
        const file = new File([blob], fileName, { type: 'text/vcard' });
        if (navigator.canShare({ files: [file] })) {
            try {
                await navigator.share({ files: [file], title: connection.name });
                return;
            } catch (err) {
                if (err?.name === 'AbortError') return;
            }
        }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// Marks an unread item read once it has been on screen for a moment.
function useSeen(ref, enabled, onSeen) {
    const cb = useRef(onSeen);
    cb.current = onSeen;
    useEffect(() => {
        const el = ref.current;
        if (!enabled || !el || typeof IntersectionObserver === 'undefined') return undefined;
        let timer = null;
        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && document.visibilityState === 'visible') {
                    timer = timer || setTimeout(() => cb.current(), 1500);
                } else if (timer) {
                    clearTimeout(timer);
                    timer = null;
                }
            },
            { threshold: 0.6 }
        );
        io.observe(el);
        return () => {
            io.disconnect();
            if (timer) clearTimeout(timer);
        };
    }, [ref, enabled]);
}

const ConnectionItem = ({ c, isFresh, onSeen, onToggleRead, onDelete, deleting }) => {
    const ref = useRef(null);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);
    useSeen(ref, !c.read, () => onSeen(c));

    const highlighted = !c.read || isFresh;
    const created = new Date(c.createdAt);

    const onSave = async () => {
        setSaving(true);
        try {
            await saveVCard(c);
        } catch {
            toast.error('Could not create the contact file');
        } finally {
            setSaving(false);
        }
    };

    const btn = 'inline-flex items-center justify-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors';

    return (
        <li
            ref={ref}
            className={`relative rounded-lg border bg-white p-4 shadow-sm transition-colors sm:p-5 ${
                highlighted ? 'border-primary-200 bg-primary-50/40' : 'border-gray-200'
            }`}
        >
            {highlighted && <span className="absolute inset-y-3 left-0 w-1 rounded-r bg-primary-600" aria-hidden="true" />}
            <div className="flex items-start gap-4">
                <div
                    className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                        highlighted ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                    aria-hidden="true"
                >
                    {initialsOf(c.name)}
                </div>

                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h3 className="truncate text-base font-semibold text-gray-900">{c.name}</h3>
                        {highlighted && (
                            <span className="rounded-full bg-primary-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                                New
                            </span>
                        )}
                        <time
                            dateTime={c.createdAt}
                            title={format(created, 'PPpp')}
                            className="ml-auto whitespace-nowrap text-xs text-gray-500"
                        >
                            {formatDistanceToNow(created, { addSuffix: true })}
                        </time>
                    </div>
                    {c.company && (
                        <p className="mt-0.5 flex items-center text-sm text-gray-600">
                            <Building2 className="mr-1.5 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            <span className="truncate">{c.company}</span>
                        </p>
                    )}

                    <div className="mt-2 flex flex-col gap-1 text-sm sm:flex-row sm:flex-wrap sm:gap-x-5">
                        {c.phone && (
                            <a href={telHref(c.phone)} className="inline-flex items-center text-primary-700 hover:underline">
                                <Phone className="mr-1.5 h-4 w-4" />
                                {c.phone}
                            </a>
                        )}
                        {c.email && (
                            <a href={`mailto:${c.email}`} className="inline-flex min-w-0 items-center text-primary-700 hover:underline">
                                <Mail className="mr-1.5 h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{c.email}</span>
                            </a>
                        )}
                    </div>

                    {c.note && (
                        <p className="mt-3 whitespace-pre-line rounded-md border-l-2 border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            {c.note}
                        </p>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={onSave}
                            disabled={saving}
                            className={`${btn} border-transparent bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60`}
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                            Save to contacts
                        </button>
                        {c.phone && (
                            <a href={telHref(c.phone)} className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50 sm:hidden`}>
                                <Phone className="h-4 w-4" />
                                Call
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={() => onToggleRead(c)}
                            className={`${btn} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
                        >
                            {c.read ? <MailOpen className="h-4 w-4" /> : <MailCheck className="h-4 w-4" />}
                            {c.read ? 'Mark unread' : 'Mark read'}
                        </button>
                        {confirming ? (
                            <span className="inline-flex items-center gap-2 rounded-md bg-red-50 px-2 py-1">
                                <span className="text-sm text-red-800">Delete?</span>
                                <button
                                    type="button"
                                    onClick={() => onDelete(c)}
                                    disabled={deleting}
                                    className="rounded-md bg-red-600 px-3 py-1 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-60"
                                >
                                    {deleting ? 'Deleting…' : 'Delete'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setConfirming(false)}
                                    className="rounded-md px-2 py-1 text-sm text-gray-600 hover:text-gray-900"
                                >
                                    Cancel
                                </button>
                            </span>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setConfirming(true)}
                                className={`${btn} border-transparent text-gray-500 hover:bg-red-50 hover:text-red-700`}
                                aria-label={`Delete ${c.name}`}
                            >
                                <Trash2 className="h-4 w-4" />
                                <span className="hidden sm:inline">Delete</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </li>
    );
};

const Connections = () => {
    const queryClient = useQueryClient();
    // Items read during this visit keep their "New" highlight until the user leaves the page.
    const [freshIds, setFreshIds] = useState(() => new Set());
    const pendingRead = useRef(new Set());

    const { data: profileData } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => (await apiMethods.users.getProfile()).data,
    });
    const exchangeEnabled = profileData?.user?.cardSettings?.exchangeEnabled !== false;

    const { data, isLoading, isError, refetch, isRefetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: LIST_KEY,
        queryFn: async ({ pageParam }) => (await apiMethods.users.getConnections({ page: pageParam, limit: PAGE_SIZE })).data,
        initialPageParam: 1,
        getNextPageParam: (last) => (last?.pagination && last.pagination.page < last.pagination.pages ? last.pagination.page + 1 : undefined),
        refetchOnWindowFocus: true,
    });

    const pages = data?.pages || [];
    const connections = pages.flatMap((p) => p.connections || []);
    const total = pages[0]?.pagination?.total ?? 0;
    const unreadCount = pages[0]?.unreadCount ?? 0;

    // Local cache patch so the UI responds instantly
    const patchItem = (id, patch) => {
        queryClient.setQueryData(LIST_KEY, (old) =>
            old
                ? {
                      ...old,
                      pages: old.pages.map((p) => ({
                          ...p,
                          connections: (p.connections || []).map((x) => (x.id === id ? { ...x, ...patch } : x)),
                      })),
                  }
                : old
        );
    };
    const bumpUnread = (delta) => {
        queryClient.setQueryData(UNREAD_KEY, (n) => Math.max(0, (n || 0) + delta));
        queryClient.setQueryData(LIST_KEY, (old) =>
            old?.pages?.length
                ? { ...old, pages: old.pages.map((p, i) => (i === 0 ? { ...p, unreadCount: Math.max(0, (p.unreadCount || 0) + delta) } : p)) }
                : old
        );
    };

    const readMutation = useMutation({
        mutationFn: ({ id, read }) => apiMethods.users.markConnection(id, read),
        onMutate: ({ id, read }) => {
            patchItem(id, { read });
            bumpUnread(read ? -1 : 1);
        },
        onError: (_e, { id, read }) => {
            patchItem(id, { read: !read });
            bumpUnread(read ? 1 : -1);
        },
        onSettled: (_d, _e, { id }) => {
            pendingRead.current.delete(id);
            queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (c) => apiMethods.users.deleteConnection(c.id),
        onSuccess: () => {
            toast.success('Connection deleted');
            // Refetch every loaded page so offsets stay consistent for "Load more"
            queryClient.invalidateQueries({ queryKey: CONNECTIONS_KEY });
        },
    });

    const onSeen = (c) => {
        if (c.read || pendingRead.current.has(c.id)) return;
        pendingRead.current.add(c.id);
        setFreshIds((s) => new Set(s).add(c.id));
        readMutation.mutate({ id: c.id, read: true });
    };

    // An explicit read/unread choice replaces the "new this visit" highlight
    const onToggleRead = (c) => {
        setFreshIds((s) => {
            const n = new Set(s);
            n.delete(c.id);
            return n;
        });
        readMutation.mutate({ id: c.id, read: !c.read });
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                        <h1 className="flex items-center text-3xl font-bold text-gray-900">
                            Connections
                            {unreadCount > 0 && (
                                <span className="ml-3 rounded-full bg-red-500 px-2.5 py-0.5 text-sm font-semibold text-white">
                                    {unreadCount} new
                                </span>
                            )}
                        </h1>
                        <p className="mt-2 text-gray-600">People who shared their contact details with you from your HexLink card.</p>
                    </div>
                    <button
                        type="button"
                        onClick={() => refetch()}
                        className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                        aria-label="Refresh"
                    >
                        <RefreshCw className={`h-4 w-4 sm:mr-2 ${isRefetching && !isFetchingNextPage ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">Refresh</span>
                    </button>
                </div>

                {!exchangeEnabled && profileData && (
                    <div className="mb-6 flex items-start rounded-lg border border-yellow-200 bg-yellow-50 p-4">
                        <AlertCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
                        <div className="text-sm text-yellow-800">
                            <p className="font-medium">Contact sharing is turned off on your card.</p>
                            <p className="mt-1">
                                Visitors can't send you their details right now.{' '}
                                <Link to="/user/profile" className="font-medium underline">
                                    Turn it on in your profile
                                </Link>
                                .
                            </p>
                        </div>
                    </div>
                )}

                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                    </div>
                ) : isError ? (
                    <div className="rounded-lg bg-white p-8 text-center shadow">
                        <p className="text-gray-700">We couldn't load your connections.</p>
                        <button type="button" onClick={() => refetch()} className="mt-3 text-sm font-medium text-primary-600 hover:text-primary-700">
                            Try again
                        </button>
                    </div>
                ) : connections.length === 0 ? (
                    <div className="rounded-lg bg-white px-6 py-12 text-center shadow">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                            <Contact className="h-8 w-8 text-primary-600" />
                        </div>
                        <h2 className="mt-4 text-lg font-semibold text-gray-900">No connections yet</h2>
                        <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
                            When someone opens your card, they can tap <span className="font-medium">“Share my contact”</span> to
                            send you their name, phone, email and a short note. They'll show up here, and we'll email you right away.
                        </p>
                        <ol className="mx-auto mt-6 grid max-w-lg gap-3 text-left text-sm text-gray-700 sm:grid-cols-3">
                            {[
                                ['1', 'They tap your HexLink or scan your QR code'],
                                ['2', 'They share their details from your card'],
                                ['3', 'You save them to your contacts in one tap'],
                            ].map(([n, t]) => (
                                <li key={n} className="flex items-start gap-2 rounded-md bg-gray-50 p-3">
                                    <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-600 text-xs font-semibold text-white">
                                        {n}
                                    </span>
                                    {t}
                                </li>
                            ))}
                        </ol>
                        <div className="mt-6 flex flex-wrap justify-center gap-3">
                            <Link
                                to="/user/card-preview"
                                className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                            >
                                <Share2 className="mr-2 h-4 w-4" />
                                Share your card
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        <p className="mb-3 text-sm text-gray-500">
                            {total} {total === 1 ? 'person has' : 'people have'} shared their contact with you
                        </p>
                        <ul className="space-y-3">
                            {connections.map((c) => (
                                <ConnectionItem
                                    key={c.id}
                                    c={c}
                                    isFresh={freshIds.has(c.id)}
                                    onSeen={onSeen}
                                    onToggleRead={onToggleRead}
                                    onDelete={(x) => deleteMutation.mutate(x)}
                                    deleting={deleteMutation.isPending && deleteMutation.variables?.id === c.id}
                                />
                            ))}
                        </ul>
                        {hasNextPage && (
                            <div className="mt-6 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => fetchNextPage()}
                                    disabled={isFetchingNextPage}
                                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60"
                                >
                                    {isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                    Load more
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Connections;
